import { Editor } from "@tiptap/core";
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { TiptapDocument, TiptapHeading } from "./services/editorInterface";

interface Heading {
  level: number;
  text: string;
  id: string;
}

const EditorTableOfContent = ({
  editor,
  editorWrapperRef,
  onClose,
}: {
  editor?: Editor;
  editorWrapperRef: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState<string>("");

  const extractHeadings = (editorContent: TiptapDocument): Heading[] => {
    const extractedHeadings = editorContent.content
      .filter((node): node is TiptapHeading => node.type === "heading")
      .map((heading) => ({
        level: heading.attrs.level,
        text: (heading.content || []).reduce((acc, curr) => {
          acc += curr.text || curr.attrs?.numValue || "";
          return acc;
        }, ""),
        id: heading.attrs.id,
      }));

    if (extractedHeadings.length === 0) return [];

    const minLevel = Math.min(
      ...extractedHeadings.map((heading) => heading.level)
    );
    return extractedHeadings.map((heading) => ({
      ...heading,
      level: heading.level - minLevel,
    }));
  };

  useEffect(() => {
    // Read the headings as soon as a save settles (and once on mount): the
    // editor already holds the document at that point, so there is nothing
    // to wait for (#12187).
    const handleSavingStateChange = () => {
      if (editor && !window.savingWorkspaceContent) {
        const links = extractHeadings(editor.getJSON() as TiptapDocument);
        setHeadings(links);
      }
    };

    window.addEventListener(
      "savingContentStateChange",
      handleSavingStateChange
    );
    handleSavingStateChange();

    return () => {
      window.removeEventListener(
        "savingContentStateChange",
        handleSavingStateChange
      );
    };
    // `editor?.trigger` used to sit in this list behind a @ts-ignore. There
    // is no such member on an Editor in either tiptap major, so the dependency
    // was always `undefined` and never re-ran the effect; dropping it changes
    // nothing but removes the suppression (#11904). The headings are refreshed
    // by the savingContentStateChange event above.
  }, [editor, editorWrapperRef]);

  const scrollToHeading = (headingId: string) => {
    const headingElement = editorWrapperRef.current?.querySelector(
      `[data-node-id="${headingId}"]`
    ) as HTMLElement;

    if (headingElement && editorWrapperRef.current) {
      editorWrapperRef.current.scrollTo({
        top: headingElement.offsetTop - 20, // Add some padding
        behavior: "smooth",
      });
      setActiveHeadingId(headingId);
    }
  };

  return (
    <div
      className="h-full bg-white shadow-sm flex flex-col opacity-70 hover:opacity-100 transition-opacity duration-200"
      style={{ width: "250px" }}
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700">Table of Contents</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors duration-200"
            title="Close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {headings.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-gray-400 text-sm">No headings found</p>
        </div>
      ) : (
        <div className="p-2 flex-1 overflow-auto">
          <nav aria-label="Table of contents">
            <ul className="space-y-1 py-1">
              {headings
                .filter((heading) => heading.text)
                .map((heading, index) => (
                  <li
                    key={index}
                    style={{
                      paddingLeft: `${heading.level * 12}px`,
                    }}
                  >
                    <button
                      className={`text-left w-full px-2 py-1.5 rounded-md text-xs transition-colors duration-200 hover:bg-gray-50 ${
                        activeHeadingId === heading.id && false
                          ? "text-blue-600 font-medium bg-blue-50 hover:bg-blue-50"
                          : "text-gray-600"
                      }`}
                      onClick={() => scrollToHeading(heading.id)}
                      aria-current={
                        activeHeadingId === heading.id ? "location" : undefined
                      }
                    >
                      <span className="block truncate">
                        {heading.text || "(Untitled heading)"}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default EditorTableOfContent;
